import { PlaybookStatus } from '@driftt/api-types'
import { A, Button, Colorway, Div, Tag } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'
import { Modal, ModalBody, ModalTitle } from '@driftt/tide-modal'
import { pluralize } from 'humanize-plus'
import React from 'react'

import { BlockUpdateVersionData } from 'api/blocks'
import CollapsibleBlockChanges from 'components/CollapsibleBlockChanges'

import { HydratedPlaybookBlockData } from '.'
import CollapsibleArchivedList from './CollapsibleArchivedList'
import { splitArchivedPlaybooks } from './helpers'

import styles from './styles.module.css'

interface Props {
  isOpen: boolean
  onClose: () => void
  consumingPlaybooks: HydratedPlaybookBlockData[]
  showSaveNotif: boolean
  majorVersionBlockChanges?: BlockUpdateVersionData
  numOutdatedPlaybooks: number
}

const PlaybookUpdatesRequiredModal = ({
  isOpen,
  onClose,
  consumingPlaybooks,
  majorVersionBlockChanges,
  showSaveNotif = false,
  numOutdatedPlaybooks,
}: Props) => {
  const { archived, active } = React.useMemo(
    () => splitArchivedPlaybooks(consumingPlaybooks),
    [consumingPlaybooks]
  )
  const hasConsumingPlaybooks = consumingPlaybooks.length > 0

  return (
    <Modal open={isOpen} onClose={onClose} size="small">
      {showSaveNotif && (
        <Div className={styles.updatesRequiredModalSaveNotif}>
          <Icon name="check-circle" width={20} height={20} />
          Reusable block saved
        </Div>
      )}
      {hasConsumingPlaybooks && (
        <ModalTitle>
          Updates required in {numOutdatedPlaybooks}{' '}
          {pluralize(numOutdatedPlaybooks, 'playbook')}
        </ModalTitle>
      )}
      <ModalBody>
        {hasConsumingPlaybooks ? (
          <>
            {consumingPlaybooks.length > 0 && (
              <>
                The reusable block structure has changed and now requires
                playbook updates.
                <Div className={styles.updatesRequiredModalSubheader}>
                  Playbooks impacted:
                </Div>
                <Div className={styles.updatesRequiredModalPbList}>
                  {active
                    .sort(
                      (a, b) =>
                        Number(
                          b.isOutdated &&
                            b.playbookStatus === PlaybookStatus.ENABLED
                        ) -
                        Number(
                          a.isOutdated &&
                            b.playbookStatus === PlaybookStatus.ENABLED
                        )
                    )
                    .map(
                      ({ playbook: pb, playbookStatus, isOutdated }, index) => {
                        return (
                          <Div key={index} className={styles.pbRow}>
                            <Div className={styles.pbDetails}>
                              <Tag
                                wrapperClass={styles.pbStatus}
                                type="status"
                                statusIsOn={
                                  playbookStatus === PlaybookStatus.ENABLED
                                }
                              />
                              <A
                                href={`/playbooks/editor/${pb.id}/visual-builder`}
                                className={styles.pbName}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {pb.name}
                              </A>
                            </Div>
                            <Div className={styles.pbDetails}>
                              {isOutdated ? (
                                <>
                                  <Div className={styles.blockVersionOutdated}>
                                    Update required
                                  </Div>
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      window.open(
                                        `/playbooks/editor/${pb.id}/visual-builder`,
                                        '_blank'
                                      )
                                    }
                                  >
                                    Update
                                  </Button>
                                </>
                              ) : (
                                <Div>Up to date</Div>
                              )}
                            </Div>
                          </Div>
                        )
                      }
                    )}
                </Div>
                {archived.length > 0 && (
                  <CollapsibleArchivedList
                    archivedPlaybooks={archived}
                    title={`Used in ${archived.length} archived ${pluralize(
                      archived.length,
                      'playbook'
                    )}`}
                  />
                )}
                {majorVersionBlockChanges && (
                  <CollapsibleBlockChanges
                    blockChanges={majorVersionBlockChanges}
                  />
                )}
              </>
            )}
          </>
        ) : (
          <Div className={styles.modalEmptyState}>
            <Colorway className={styles.modalEmptyStateColorway} type="2">
              <Icon name="cw-bots" width="80" height="80" />
            </Colorway>
            <Div className={styles.modalEmptyStateSubheader}>
              Ready to add this reusable block to a playbook?
            </Div>
            Open a playbook to insert this reusable block anywhere in the bot
            flow.
            <Button
              className={styles.modalEmptyStateButton}
              target="_blank"
              href="/playbooks"
            >
              View playbooks
            </Button>
          </Div>
        )}
      </ModalBody>
    </Modal>
  )
}

export default PlaybookUpdatesRequiredModal
